import React, { useState } from 'react';
import './ModulesPage.css';
import { Ear, Eye, Brain, BookOpen, MessageSquare, Clock, Mic, Lightbulb, User } from 'lucide-react';
import SoundTest from './modules/sound/SoundTest';
import MusicPerception from './modules/musicperception/MusicPerception';
import RhythmTest from './modules/rhythm/RhythmTest';
import PhonemicPerception from './modules/phonemic/PhonemicPerception';
import SoundInWord from './modules/phonemic/SoundInWord';
import SoundPosition from './modules/phonemic/SoundPosition';
import VisionTest from './modules/vision/VisionTest';
import SizeTest from './modules/size/SizeTest';
import VisionPerception from './modules/vision/VisionPerception';
import SpatialDirections from './modules/space/SpatialDirections';
import Puzzle from './modules/space/Puzzle';
import SeguinBoard from './modules/space/SeguinBoard';
import Mentalrotation from './modules/space/Mentalrotation';
import VocabularyTest from './modules/impressivespeech/VocabularyTest';
import WordFormation from './modules/impressivespeech/WordFormation';
import WordFormationDiminutive from './modules/impressivespeech/WordFormationDiminutive';
import AnimalBabies from './modules/impressivespeech/AnimalBabies';
import VerbsFormation from './modules/impressivespeech/VerbsFormation';
import NegationsTest from './modules/impressivespeech/NegationsTest';
import MultiStepInstr from './modules/impressivespeech/MultiStepInstr';
import SentenceComprehension from './modules/SentenceComprehension/SentenceComprehension';
import StorySequence from './modules/story/StorySequence';
import Generalization from './modules/activespeech/Generalization';
import Antonyms from './modules/activespeech/Antonyms';
import Verbs from './modules/activespeech/Verbs';
import SyllableStructure from './modules/activespeech/SyllableStructure';
import DailyRoutine from './modules/awareness/DailyRoutine';
import Seasons from './modules/awareness/Seasons';
import DaysOfWeek from './modules/awareness/DaysOfWeek';
import Clocks from './modules/awareness/Clocks';
import ClockBuilder from './modules/awareness/ClockBuilder';
import FindOddOne from './modules/logic/FindOddOne';
import MissingItem from './modules/logic/MissingItem';
import GroupPictures from './modules/logic/GroupPictures';
import LifeStages from './modules/logic/LifeStages';
import AnimalSorting from './modules/logic/AnimalSorting';

import ReportPreview from './components/ReportPreview';
import { supabase } from './supabaseClient';

// ===== СВОЯ ПОСЛЕДОВАТЕЛЬНОСТЬ УПРАЖНЕНИЙ ДЛЯ КАЖДОГО МОДУЛЯ =====
const MODULE_FLOWS = {
  module1: ['sound', 'musicperception', 'rhythm', 'phonemicperception', 'soundinword', 'soundposition'],
  module2: ['vision', 'size', 'visionperception'],
  module3: ['space', 'puzzle', 'seguinboard', 'mentalrotation'],
  module4: ['vocabulary', 'wordformation', 'wordformationdiminutive', 'animalbabies', 'verbsformation', 'negationstest', 'multistepinstr', 'sentencecomprehension', 'storysequence'],
  module5: ['generalization', 'antonyms', 'verbs', 'syllablestructure'],
  module6: ['dailyroutine', 'seasons', 'daysofweek', 'clocks', 'clockbuilder'],
  module7: ['findoddone', 'missingitem', 'grouppictures', 'lifestages', 'animalsorting'],
  module8: [], // TODO: тест готов нужно подключить компонент и поправить UI — до этого остаётся статус "Скоро"
};

const MODULE_ORDER = ['module1', 'module2', 'module3', 'module4', 'module5', 'module6', 'module7', 'module8'];

const MODULE_EXPECTED_TESTS = {
  module1: ['SoundTest', 'MusicPerception', 'RhythmTest', 'PhonemicPerception', 'SoundInWord', 'SoundPosition'],
  module2: ['VisionTest', 'SizeTest', 'VisionPerception'],
  module3: ['SpatialDirections', 'Puzzle', 'SeguinBoard', 'Mentalrotation'],
  module4: ['VocabularyTest', 'WordFormation', 'WordFormationDiminutive', 'AnimalBabies', 'VerbsFormation', 'NegationsTest', 'MultiStepInstr', 'SentenceComprehension', 'StorySequence'],
  module5: ['Generalization', 'Antonyms', 'Verbs', 'SyllableStructure'],
  module6: ['DailyRoutine', 'Seasons', 'DaysOfWeek', 'Clocks', 'ClockBuilder'],
  module7: ['FindOddOne', 'MissingItem', 'GroupPictures', 'LifeStages', 'AnimalSorting'],
  module8: [],
};

// Описания — средняя длина: не одно слово, но и не полный список подпунктов
const MODULE_META = [
  { key: 'module1', accent: 'm1', icon: Ear, name: 'Слух и фонематика', desc: 'Различение звуков, музыки и ритма, фонематическое восприятие речи', chips: ['Звуки', 'Музыка', 'Ритм', 'Фонемы'], time: '5–10 мин' },
  { key: 'module2', accent: 'm2', icon: Eye, name: 'Зрительное восприятие', desc: 'Различение цвета, формы и величины, восприятие в условиях наложения', chips: ['Цвет', 'Форма', 'Величина', 'Наложение'], time: '6–12 мин' },
  { key: 'module3', accent: 'm3', icon: Brain, name: 'Пространственное мышление', desc: 'Ориентировка в пространстве и в схеме тела, пазлы, мысленное вращение фигур', chips: ['Пространство', 'Пазлы', 'Вращение'], time: '8–15 мин' },
  { key: 'module4', accent: 'm4', icon: BookOpen, name: 'Импрессивная речь', desc: 'Понимание словоизменения, отрицаний, многоступенчатых инструкций и связной речи', chips: ['Словарь', 'Отрицания', 'Инструкции', 'Связная речь'], time: '15–20 мин' },
  { key: 'module5', accent: 'm5', icon: MessageSquare, name: 'Активная речь', desc: 'Лексика, грамматика, словообразование и слоговая структура слова', chips: ['Лексика', 'Грамматика', 'Словообразование', 'Слоги'], time: '15–20 мин' },
  { key: 'module6', accent: 'm6', icon: Clock, name: 'Общая осведомлённость', desc: 'Знание частей суток, времён года, дней недели и времени', chips: ['Сутки', 'Сезоны', 'Дни недели', "Время"], time: '5–10 мин' },
  { key: 'module7', accent: 'm7', icon: Lightbulb, name: 'Мышление', desc: 'Классификация и логические ряды: «4 лишний», «чего не хватает», «группы», «этапы»', chips: ['4 лишний', 'Чего не хватает?', 'Группы','Этапы', 'Последовательности'  ], time: '7–10 мин' },
  { key: 'module8', accent: 'm8', icon: Mic, name: 'Артикуляция', desc: 'Артикуляционная гимнастика, произношение гласных и согласных звуков', chips: ['Гимнастика', 'Гласные', 'Согласные'], time: '6–12 мин' },
];

// ===== ЦЕНООБРАЗОВАНИЕ =====

// в MODULE_FLOWS.module8 ещё пустые — реальный тест нужно туда добавить до релиза,
// иначе выбор только этого модуля и старт теста не дадут ни одного упражнения.
const MODULE_FORCE_ACTIVE = ['module7'];
const ACTIVE_MODULE_KEYS = MODULE_ORDER.filter(
  (k) => (MODULE_FLOWS[k] && MODULE_FLOWS[k].length > 0) || MODULE_FORCE_ACTIVE.includes(k)
);
const MODULE_PRICE = 100;
const BUNDLE_PRICE = 600;

const calculateTotal = (selected) => {
  if (selected.length === ACTIVE_MODULE_KEYS.length) return BUNDLE_PRICE;
  return selected.length * MODULE_PRICE;
};

const ModulesPage = ({ userData, onBack, onRequireLogin }) => {
  const [currentModule, setCurrentModule] = useState('null');
  const [currentAgeGroup, setCurrentAgeGroup] = useState(userData?.ageGroup || '4');
  const [selectedModules, setSelectedModules] = useState([]);

  const [testResults, setTestResults] = useState({});
  const [testDetails, setTestDetails] = useState({});

  const [activeFlow, setActiveFlow] = useState(null);
  const [activeFlowExpected, setActiveFlowExpected] = useState([]);
  const [flowIndex, setFlowIndex] = useState(0);

  const [showReport, setShowReport] = useState(false);
  const [finalResults, setFinalResults] = useState({});
  const [finalDetails, setFinalDetails] = useState({});
  const [reportProfile, setReportProfile] = useState(null);
  const [reportExpectedTests, setReportExpectedTests] = useState([]);

  const toggleModuleSelection = (key) => {
    if (!ACTIVE_MODULE_KEYS.includes(key)) return;
    setSelectedModules((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    setSelectedModules((prev) =>
      prev.length === ACTIVE_MODULE_KEYS.length ? [] : [...ACTIVE_MODULE_KEYS]
    );
  };

    const startSelectedModules = async () => {
    // 1. ПРОВЕРЯЕМ АВТОРИЗАЦИЮ
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      // Если не авторизован — отправляем на страницу входа
      onRequireLogin();
      return; // Останавливаем выполнение, тест не начинаем
    }

    // 2. ЕСЛИ АВТОРИЗОВАН — ЗАПУСКАЕМ ТЕСТ (старая логика)
    const orderedKeys = MODULE_ORDER.filter((k) => selectedModules.includes(k));
    const combinedFlow = orderedKeys.flatMap((k) => MODULE_FLOWS[k]);
    const combinedExpected = orderedKeys.flatMap((k) => MODULE_EXPECTED_TESTS[k]);
    if (combinedFlow.length === 0) return;

    setTestResults({});
    setTestDetails({});
    setActiveFlow(combinedFlow);
    setActiveFlowExpected(combinedExpected);
    setFlowIndex(0);
    setCurrentModule(combinedFlow[0]);
  };

  const goToNextModule = (result) => {
    let updatedResults = testResults;
    let updatedDetails = testDetails;

    if (result && result.testId) {
      updatedResults = { ...testResults, [result.testId]: result.score };
      setTestResults(updatedResults);

      if (result.details) {
        updatedDetails = { ...testDetails, [result.testId]: result.details };
        setTestDetails(updatedDetails);
      }

      console.log(`Тест ${result.testId} завершен с результатом: ${result.score}%`);
    }

    const nextIndex = flowIndex + 1;

    if (activeFlow && nextIndex < activeFlow.length) {
      setFlowIndex(nextIndex);
      setCurrentModule(activeFlow[nextIndex]);
    } else {
      console.log('Все выбранные модули завершены. Результаты:', updatedResults, 'Детализация:', updatedDetails);
      const profile = {
        name: userData?.name || 'Имя Ребенка',
        age: currentAgeGroup + ' лет',
        anamnesis: userData?.anamnesis || 'Не указано',
      };

      setFinalResults(updatedResults);
      setFinalDetails(updatedDetails);
      setReportProfile(profile);
      setReportExpectedTests(activeFlowExpected.length > 0 ? activeFlowExpected : Object.keys(updatedResults));
      setCurrentModule('null');
      setShowReport(true);
    }
  };

  const handleCloseReport = () => {
    setShowReport(false);
    setActiveFlow(null);
    setActiveFlowExpected([]);
    setFlowIndex(0);
    setSelectedModules([]);
    onBack();
  };

  if (currentModule === 'sound') return <SoundTest ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'musicperception') return <MusicPerception ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'rhythm') return <RhythmTest ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'phonemicperception') return <PhonemicPerception ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'soundinword') return <SoundInWord ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'soundposition') return <SoundPosition ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'vision') return <VisionTest ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'size') return <SizeTest onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'visionperception') return <VisionPerception ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'space') return <SpatialDirections ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'puzzle') return <Puzzle ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'seguinboard') return <SeguinBoard ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'mentalrotation') return <Mentalrotation ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'vocabulary') return <VocabularyTest ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'wordformation') return <WordFormation ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'wordformationdiminutive') return <WordFormationDiminutive ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'animalbabies') return <AnimalBabies ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'verbsformation') return <VerbsFormation ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} onStartPause={(state) => {}} />;
  if (currentModule === 'negationstest') return <NegationsTest ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'multistepinstr') return <MultiStepInstr ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'sentencecomprehension') return <SentenceComprehension ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'storysequence') return <StorySequence ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'generalization') return <Generalization ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'antonyms') return <Antonyms ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'verbs') return <Verbs ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'syllablestructure') return <SyllableStructure ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'dailyroutine') return <DailyRoutine ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'seasons') return <Seasons ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'daysofweek') return <DaysOfWeek ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'clocks') return <Clocks ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'clockbuilder') return <ClockBuilder ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'findoddone') return <FindOddOne ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'missingitem') return <MissingItem ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'grouppictures') return <GroupPictures ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'lifestages') return <LifeStages ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;
  if (currentModule === 'animalsorting') return <AnimalSorting ageGroup={currentAgeGroup} onBack={onBack} onComplete={goToNextModule} />;


  const allSelected = selectedModules.length === ACTIVE_MODULE_KEYS.length;
  const oneShortOfBundle = selectedModules.length === ACTIVE_MODULE_KEYS.length - 1;
  const total = calculateTotal(selectedModules);

  return (
    <>
      <div className="modules-container">
        <div className="modules-card">
          <header className="modules-header">
            <button className="back-btn" onClick={onBack}>← Назад</button>
            <div className="modules-user">
              <User size={16} strokeWidth={2} />
              <select
                value={currentAgeGroup}
                onChange={(e) => setCurrentAgeGroup(e.target.value)}
                style={{ marginLeft: '8px', border: 'none', background: 'transparent', fontWeight: '600', fontSize: 'inherit', cursor: 'pointer' }}
              >
                <option value="4">4 года</option>
                <option value="5">5 лет</option>
                <option value="6">6 лет</option>
                <option value="7-8">7-8 лет</option>
                
              </select>
            </div>
          </header>

          <p className="modules-duration-note">
            Полное исследование занимает 60–90 минут — можно пройти сразу или по частям,
            в удобном для ребёнка темпе
          </p>

          <div className="modules-start-bar modules-start-bar-top">
            <div className="modules-action-row">
              <button
                className={`modules-select-all-btn ${allSelected ? 'active' : ''}`}
                onClick={toggleSelectAll}
              >
                {allSelected ? '✕ Снять все' : `✓ Выбрать все (${ACTIVE_MODULE_KEYS.length} модулей — ${BUNDLE_PRICE} ₽)`}
              </button>

              <button
                className="modules-start-selected-btn"
                disabled={selectedModules.length === 0}
                onClick={startSelectedModules}
              >
                {selectedModules.length > 0
                  ? `Начать тест — ${total} ₽`
                  : 'Выберите модули'}
              </button>
            </div>
          </div>

          <div className="modules-hero">
            <div className="modules-grid">
              {MODULE_META.map((m) => {
                const isDisabled = !ACTIVE_MODULE_KEYS.includes(m.key);
                const isSelected = selectedModules.includes(m.key);
                return (
                  <div
                    key={m.key}
                    className={`module-card ${m.accent} ${isDisabled ? 'module-card-disabled' : 'active'} ${isSelected ? 'module-card-selected' : ''}`}
                    onClick={() => toggleModuleSelection(m.key)}
                  >
                    <div className="card-top">
                      <div className="icon-box">
                        <m.icon size={22} strokeWidth={1.8} />
                      </div>
                      <div className="badges">
                        <span className="time-badge">⏱ {m.time}</span>
                        {!isDisabled && <span className="price-badge">{MODULE_PRICE} ₽</span>}
                        {isDisabled && <span className="soon-tag">Скоро</span>}
                      </div>
                    </div>

                    <h3 className="card-name">{m.name}</h3>
                    <p className="card-desc">{m.desc}</p>
                    <div className="chips">
                      {m.chips.map((c) => <span key={c} className="chip">{c}</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {oneShortOfBundle && (
            <div className="modules-upsell-banner">
              💡 Добавьте ещё 1 модуль — и получите все {ACTIVE_MODULE_KEYS.length} за те же {BUNDLE_PRICE} ₽
            </div>
          )}
        </div>
      </div>

      {showReport && (
        <ReportPreview
          profile={reportProfile}
          test_scores={finalResults}
          test_details={finalDetails}
          expectedTests={reportExpectedTests}
          onClose={handleCloseReport}
        />
      )}
    </>
  );
};

export default ModulesPage;